import type { PublicPlans } from '../lib/api.ts';
import { T, tx, type Lang } from '../lib/i18n.ts';
import { PLAN_ROWS, formatQuota, yearlySaving } from '../lib/plans.ts';
import { QUOTA_INERT } from '../lib/quota-wiring.ts';

/**
 * 免费版和 VIP 的对比表。首页和游客的账号页用。
 *
 * ════════════════════════════════════════════════════════
 * 只比数量，不比功能 —— 这一点写在表下面最显眼的地方
 * ════════════════════════════════════════════════════════
 * 照原型的 `planCompare`：功能免费版全都有，VIP 只是能同时盯更多东西、
 * 价格刷新更快。把功能拿来分档，等于让一个在连亏的人为「熔断」付钱 ——
 * 这个产品不该那么做。
 *
 * **数字全部来自 `/api/public/plans`**（数据库里的 quota_default 和 admin_param），
 * 这里只管三语的名字和怎么显示（lib/plans.ts）。运营改了额度，这张表不用发版就跟着变。
 */

const YES = '✓';

export function PlanTable({ plans, lang }: { plans: PublicPlans | null; lang: Lang }): React.ReactElement {
  if (!plans) {
    return <div className="hint" style={{ padding: 12 }}>{tx(T('正在读价目表…', '正在讀價目表…', 'Loading plans…'), lang)}</div>;
  }
  const byKey = new Map(plans.quotas.map(q => [q.key, q]));
  const saving = yearlySaving(plans.prices);

  return (
    <div className="plan-table">
      <table className="tbl compare">
        <thead>
          <tr>
            <th><span className="sr-only">{tx(T('项目', '項目', 'Item'), lang)}</span></th>
            <th className="r" scope="col">{tx(T('免费版', '免費版', 'Free'), lang)}</th>
            <th className="r" scope="col">VIP</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row" style={{ textAlign: 'left' }}>{tx(T('价格', '價格', 'Price'), lang)}</th>
            <td className="r num">{tx(T('一直免费', '一直免費', 'Free forever'), lang)}</td>
            <td className="r num">
              {plans.prices.yearUsd} USDT / {tx(T('年', '年', 'yr'), lang)}
              <div className="hint">
                {tx(T('或', '或', 'or'), lang)} {plans.prices.monthUsd} USDT / {tx(T('月', '月', 'mo'), lang)}
                {saving !== null ? ` · ${tx(T('年付省', '年付省', 'save'), lang)} ${saving} USDT` : ''}
              </div>
            </td>
          </tr>
          {PLAN_ROWS.map(row => {
            const q = byKey.get(row.key);
            if (!q) return null;
            /* 这一行的数字现在不起作用（见 quota-wiring.ts 的 QUOTA_INERT）：标一个 *，表下面说清楚。
               价目表是拿来比档位、促成付款的，一行不起作用的差异印在这里等于拿假的差异收钱 */
            const inert = row.key in QUOTA_INERT;
            return (
              <tr key={row.key}>
                <th scope="row" style={{ textAlign: 'left' }}>{tx(row.label, lang)}{inert ? <sup> *</sup> : null}</th>
                <td className="r num">{formatQuota(q, 'free', lang)}</td>
                <td className="r num">{formatQuota(q, 'vip', lang)}</td>
              </tr>
            );
          })}
          <tr>
            {/* 原来写「16 条交易纪律」—— 16 条里网页版在跑的是一半，比的是「两档一样」，不报总数 */}
            <th scope="row" style={{ textAlign: 'left' }}>{tx(T('全部交易纪律', '全部交易紀律', 'Every discipline rule'), lang)}</th>
            <td className="r"><span className="up">{YES}</span></td>
            <td className="r"><span className="up">{YES}</span></td>
          </tr>
          <tr>
            <th scope="row" style={{ textAlign: 'left' }}>{tx(T('全部配色与外观', '全部配色與外觀', 'All themes and appearance'), lang)}</th>
            <td className="r"><span className="up">{YES}</span></td>
            <td className="r"><span className="up">{YES}</span></td>
          </tr>
        </tbody>
      </table>
      {PLAN_ROWS.filter(r => r.key in QUOTA_INERT && byKey.has(r.key)).map(r => (
        <div key={r.key} className="hint" style={{ marginTop: 8 }}>
          * {tx(r.label, lang)} · {tx(QUOTA_INERT[r.key]!, lang)}
        </div>
      ))}
      <div className="hint" style={{ marginTop: 10, lineHeight: 1.7 }}>
        {tx(T(
          '只比数量，不比功能 —— 免费版功能全都有。VIP 只是能同时盯更多东西、价格查得更勤。',
          '只比數量，不比功能 —— 免費版功能全都有。VIP 只是能同時盯更多東西、價格查得更勤。',
          'Same features on both. VIP just lets you watch more at once and checks prices more often.'
        ), lang)}
        {' '}
        {plans.purchasable
          ? tx(T('用 BSC 或 Solana 上的 USDT / USDC 付款，不用绑卡、不用实名。',
            '用 BSC 或 Solana 上的 USDT / USDC 付款，不用綁卡、不用實名。',
            'Pay with USDT / USDC on BSC or Solana. No card, no KYC.'), lang)
          : tx(T('VIP 付款即将开放（BSC / Solana 上的 USDT、USDC，不用绑卡、不用实名）。开放之前所有人都是免费版。',
            'VIP 付款即將開放（BSC / Solana 上的 USDT、USDC，不用綁卡、不用實名）。開放之前所有人都是免費版。',
            'VIP checkout opens soon (USDT / USDC on BSC or Solana, no card, no KYC). Until then everyone is on Free.'), lang)}
      </div>
    </div>
  );
}
